import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { permissions } from '@/access-control.config';
import { useAuth } from '@/chassis/core/auth/hooks';
import type { RepresentationSummary } from '../MyDashboard/service';
import { suppliersService } from '@/domains/pages/3.2-registration-module-company/Suppliers/service';
import { publicAgenciesService } from '@/domains/pages/3.2-registration-module-company/PublicAgencies/service';
import { DocumentNumberUtils } from '@/chassis/core/utils';
import { PUBLIC_AGENCY_REPRESENTATION_TYPE_OPTIONS, REPRESENTATION_TYPE_OPTIONS } from '@/domains/pages/3.3-registration-module-representation/constants';
import { pickActiveSubscription, subscriptionsService } from '@/domains/pages/5.3-subscription-module-order/Subscriptions/service';
import { isSubscriptionCurrent, subscriptionScopeOf } from '@/domains/pages/5.3-subscription-module-order/plan-scope';
import { subscriptionPlansService, type SubscriptionPlanRecord } from '@/domains/pages/5.3-subscription-module-order/SubscriptionPlans/service';
import { userSharedService } from './service';
import { DataSanitizationUtils } from '@/chassis/core/utils/datasanitization-utils';
import {
    buildEntityCacheKey,
    resolveRepresentationEntity,
    sanitizeEntityIdForType,
    type RepresentationEntityType,
} from './entity-utils';
import { isRepresentationActive } from './representation-activity';
import { normalizeId, resolvePreferredRepresentationId } from './representation-id';
import { http } from '@/chassis/data/services/http-client';

export const CITIZEN_VIEW_CARD_ID = 'citizen-view';
export const ADMIN_VIEW_CARD_ID = 'admin-view';
export const PORTAL_REPRESENTATION_CHANGE_EVENT = 'cdcb.portal.representation.change.request';
// Disparado quando uma assinatura e contratada/ativada (webhook confirmado na tela de retorno).
// O provider escuta e forca o refresh do cache de assinaturas — sem isso o modal de selecao de
// visualizacao continua mostrando o fornecedor "sem plano" mesmo apos contratar.
export const PORTAL_SUBSCRIPTION_CHANGE_EVENT = 'cdcb.portal.subscription.change';
const SELECTED_STORAGE_KEY = 'cdcb.portal.selectedRepresentationId';

type SelectedSetter = string | null | ((current: string | null) => string | null);
type SetRepresentationsOptions = {
    forceEntityReload?: boolean;
};

interface RepresentationEntitySnapshot {
    entityType: RepresentationEntityType;
    entityId: string;
    name?: string | null;
    document?: string | null;
    secondaryName?: { label: string; value: string } | null;
}

export interface RepresentationSelectionCard {
    id: string;
    name: string;
    document: string | null;
    secondaryNameLabel?: string | null;
    secondaryNameValue?: string | null;
    representationType: string | null;
    representationTypeLabel: string | null;
    entityType: RepresentationEntityType | null;
    entityId?: string | null;
    entitySupplierId?: string | null;
    hasActiveSubscription?: boolean;
    subscriptionPlanName?: string | null;
    subscriptionValidUntil?: string | null;
}

/**
 * Rótulo do TIPO DE ACESSO (papel) que o usuário está usando, conforme a
 * representação/visão selecionada: Cidadão · Administrador · Fornecedor ·
 * Pregoeiro/Agente de Contratação · Autoridade Superior · Equipe de Apoio.
 */
export function resolveAccessRoleLabel(
    card: RepresentationSelectionCard | null | undefined,
    selectedId?: string | null,
): string {
    if (selectedId === CITIZEN_VIEW_CARD_ID) return 'Cidadão';
    if (selectedId === ADMIN_VIEW_CARD_ID) return 'Administrador';
    if (card?.entityType === 'supplier') return 'Fornecedor';
    if (card?.entityType === 'public_agency') return card.representationTypeLabel || 'Órgão público';
    return card?.representationTypeLabel || 'Cidadão';
}

// FIX9 05/08 (Flávio): o card do órgão mostrava o TIPO JURÍDICO da
// representação ("Representação Legal") no lugar da FUNÇÃO do servidor.
// O papel real vem do login (user.roles), resolvido dos roleassignments.
const AGENCY_ROLE_LABELS: Array<[string, string]> = [
    ['PREGOEIRO', 'Pregoeiro(a)/Agente de Contratação'],
    ['AGENTE_CONTRATACAO', 'Agente de Contratação'],
    ['AUTORIDADE_SUPERIOR', 'Autoridade Superior'],
    ['EQUIPE_APOIO', 'Equipe de Apoio'],
];
/**
 * 15/10: o FIX9 acima resolveu o rotulo errado, mas assumiu UM card por orgao.
 * Quando a mesma pessoa tem PREGOEIRO + AUTORIDADE_SUPERIOR + EQUIPE_APOIO no
 * MESMO orgao (tres representacoes), os tres cards saiam identicos — porque
 * `user.roles` e global do login, nao do card, e resolveAgencyRoleLabel devolve
 * sempre o primeiro match da lista.
 *
 * Aqui o tipo do PROPRIO card desempata, e so quando ele e uma funcao
 * conhecida. Se for tipo juridico ("Representacao Legal"), devolve null e cai
 * no comportamento do FIX9 — que era exatamente o caso que o Flavio reportou.
 */
const resolveCardRoleLabel = (representationType?: string | null): string | null => {
    if (!representationType) return null;
    const code = String(representationType).toUpperCase();
    return AGENCY_ROLE_LABELS.find(([c]) => c === code)?.[1] ?? null;
};

const resolveAgencyRoleLabel = (roles?: string[] | null): string | null => {
    if (!Array.isArray(roles) || roles.length === 0) return null;
    const set = new Set(roles.map((r) => String(r).toUpperCase()));
    for (const [code, label] of AGENCY_ROLE_LABELS) {
        if (set.has(code)) return label;
    }
    return null;
};

const normalizeSupplierId = (raw?: string | null): string | null => {
    const sanitized = sanitizeEntityIdForType('supplier', raw);
    return sanitized ? DataSanitizationUtils.ensureResourcePrefix('SUPPLIER', sanitized) : null;
};

const stripSupplierPrefix = (value: string): string => {
    const upperPrefix = 'SUPPLIER:';
    if (value.toUpperCase().startsWith(upperPrefix)) {
        return value.slice(upperPrefix.length);
    }
    return value;
};

const resolveEntityBinding = resolveRepresentationEntity;

const sortSubscriptionsByRecency = <T extends { createdAt?: string; startDate?: string }>(items: T[]): T[] => {
    const ts = (value?: string) => {
        if (!value) return 0;
        const time = new Date(value).getTime();
        return Number.isNaN(time) ? 0 : time;
    };
    return [...items].sort((a, b) => {
        const createdDiff = ts(b.createdAt) - ts(a.createdAt);
        if (createdDiff !== 0) return createdDiff;
        return ts(b.startDate) - ts(a.startDate);
    });
};

const normalizePlanIdentifiers = (rawPlanId?: string | null): { normalizedId: string; sanitizedId: string } | null => {
    const normalizedId = DataSanitizationUtils.normalizeResourceIdValue(rawPlanId);
    if (!normalizedId) return null;
    const sanitizedId = DataSanitizationUtils.splitResourceId(normalizedId)?.remainder ?? normalizedId;
    if (!sanitizedId) return null;
    return { normalizedId, sanitizedId };
};

interface PortalRepresentationSelectionContextValue {
    representations: RepresentationSummary[];
    selectedRepresentationId: string | null;
    setRepresentations: (items: RepresentationSummary[], options?: SetRepresentationsOptions) => Promise<RepresentationSummary[]>;
    setSelectedRepresentationId: (value: SelectedSetter) => void;
    requestRepresentationChange: (nextId: string) => void;
    refreshRepresentations: (force?: boolean) => Promise<RepresentationSummary[]>;
    loading: boolean;
    error: string | null;
    representationCards: RepresentationSelectionCard[];
}

const PortalRepresentationSelectionContext = createContext<PortalRepresentationSelectionContextValue | undefined>(undefined);

const activeRepresentation = (item: RepresentationSummary | undefined | null): boolean => isRepresentationActive(item);

const readStoredSelectedRepresentationId = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        const stored = window.localStorage.getItem(SELECTED_STORAGE_KEY);
        return normalizeId(stored);
    } catch {
        return null;
    }
};

const persistSelectedRepresentationId = (value: string | null) => {
    if (typeof window === 'undefined') return;
    const normalized = normalizeId(value);
    try {
        if (normalized) {
            window.localStorage.setItem(SELECTED_STORAGE_KEY, normalized);
        } else {
            window.localStorage.removeItem(SELECTED_STORAGE_KEY);
        }
    } catch {
        // ignore persistence errors
    }
};

/** Bug #92 (Flavio, 03/06/2026): persiste por CONTA no BE, fire-and-forget.
 *  Chamado a cada troca de perfil pelo usuario; falhas sao silenciadas pra nao
 *  bloquear UI (worst case: proximo login cai em Cidadao, comportamento antigo). */
const persistSelectedRepresentationToAccount = (value: string | null): void => {
    void http.patch('/authentication/last-selected-representation', { representationId: value })
        .catch(() => { /* fire-and-forget */ });
};

export function RepresentationSelectionProvider({ children }: { children: React.ReactNode }) {
    const { user, hasPermission, status: authStatus, isGuest } = useAuth();
    const authReady = authStatus === 'ready';
    const isSystemAdmin = user?.systemAdmin === true;
    const canUseAdminView = useMemo(() => isSystemAdmin || hasPermission(permissions.adminAreaRead), [hasPermission, isSystemAdmin]);
    const [representations, setRepresentationsState] = useState<RepresentationSummary[]>([]);
    const [selectedRepresentationId, internalSetSelectedRepresentationId] = useState<string | null>(() => {
        const stored = readStoredSelectedRepresentationId();
        return stored ?? CITIZEN_VIEW_CARD_ID;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [entityCache, setEntityCache] = useState<Record<string, RepresentationEntitySnapshot>>({});
    const [subscriptionCache, setSubscriptionCache] = useState<Record<string, { hasActiveSubscription: boolean; planName: string | null; validUntil: string | null }>>({});
    const entityCacheRef = useRef<Record<string, RepresentationEntitySnapshot>>({});
    const subscriptionCacheRef = useRef<Record<string, { hasActiveSubscription: boolean; planName: string | null; validUntil: string | null }>>({});
    const mountedRef = useRef(true);
    const hasFetchedOnceRef = useRef(false);
    const previousUserIdRef = useRef<string | undefined>(undefined);
    // Bug #92: captura se o localStorage estava vazio no mount. Usado pra
    // decidir se aplicamos o fallback de conta (user.lastSelectedRepresentationId)
    // — local tem precedencia sobre BE quando o usuario ja fez uma escolha neste
    // dispositivo.
    const localStorageWasEmptyAtMount = useRef<boolean>(readStoredSelectedRepresentationId() === null);
    const hydratedFromAccountRef = useRef<boolean>(false);
    const representationTypeLabels = useMemo(
        () => new Map<string, string>([...REPRESENTATION_TYPE_OPTIONS, ...PUBLIC_AGENCY_REPRESENTATION_TYPE_OPTIONS]),
        [],
    );

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const loadRepresentationEntities = useCallback(
        async (summaries: RepresentationSummary[], options?: { force?: boolean }) => {
            const existingKeys = options?.force ? [] : Object.keys(entityCacheRef.current ?? {});
            const result = await userSharedService.fetchRepresentationEntities(summaries, existingKeys, { force: options?.force === true });

            if (!mountedRef.current) {
                return;
            }

            if (!result.length) {
                return;
            }

            setEntityCache((previous) => {
                let changed = false;
                const next = { ...previous };

                for (const { cacheKey, info } of result) {
                    const existing = next[cacheKey];
                    if (
                        !existing ||
                        existing.name !== info.name ||
                        existing.document !== info.document ||
                        existing.entityId !== info.entityId ||
                        existing.entityType !== info.entityType ||
                        (existing.secondaryName?.label !== info.secondaryName?.label || existing.secondaryName?.value !== info.secondaryName?.value)
                    ) {
                        next[cacheKey] = info;
                        entityCacheRef.current[cacheKey] = info;
                        changed = true;
                    }
                }

                return changed ? next : previous;
            });
        },
        [userSharedService],
    );

    const resolvePlanRecord = useCallback(async (planId: string): Promise<SubscriptionPlanRecord | null> => {
        const identifiers = normalizePlanIdentifiers(planId);
        if (!identifiers) return null;

        const attempts = identifiers.normalizedId === identifiers.sanitizedId
            ? [identifiers.normalizedId]
            : [identifiers.normalizedId, identifiers.sanitizedId];

        for (const candidateId of attempts) {
            try {
                return await subscriptionPlansService.get(candidateId);
            } catch (error: any) {
                const status = error?.status ?? error?.response?.status ?? error?.cause?.status;
                if (status !== 404) {
                    throw error;
                }
            }
        }

        return null;
    }, []);

    const loadSupplierSubscriptions = useCallback(
        async (summaries: RepresentationSummary[]) => {
            const supplierIds = new Set<string>(summaries.map((summary) => normalizeSupplierId(summary.entitySupplierId)).filter((value): value is string => Boolean(value)));

            if (!supplierIds.size) return;

            const results = await Promise.all(
                Array.from(supplierIds).map(async (supplierId) => {
                    try {
                        const list = await subscriptionsService.list({ supplierId, active: true });
                        const items = list?.items ?? [];
                        if (!items.length) {
                            return { supplierId, hasActiveSubscription: false, planName: null, validUntil: null } as const;
                        }

                        const sorted = sortSubscriptionsByRecency(items);
                        // Planos por orgao: o nacional (se houver) e o que aparece no cabecalho.
                        const preferred =
                            sorted.find((sub) => isSubscriptionCurrent(sub) && subscriptionScopeOf(sub) === 'NATIONAL') ?? pickActiveSubscription(sorted);
                        if (!preferred) {
                            return { supplierId, hasActiveSubscription: false, planName: null, validUntil: null } as const;
                        }

                        const validUntil = (preferred.endDate || (preferred as any).expiresAt || preferred.nextBillingDate || null) as string | null;

                        const rawPlanId = preferred.planId ? String(preferred.planId) : '';
                        // many list endpoints already include planName; avoid extra requests when possible
                        let planName: string | null = (preferred as any)?.plan?.planName?.trim() || (preferred as any)?.planName?.toString().trim() || null;

                        if (!planName && rawPlanId) {
                            try {
                                const plan = await resolvePlanRecord(rawPlanId);
                                planName = plan?.planName?.trim() || null;
                            } catch (error) {
                                console.error('Failed to resolve subscription plan', { supplierId, planId: rawPlanId, error });
                            }
                        }

                        // Nunca expor identificadores técnicos como rótulo visível do plano.
                        if (planName && planName.includes(':')) {
                            planName = null;
                        }

                        // Plano de orgao: o rotulo diz de qual orgao e quantos outros orgaos tem.
                        if (planName && subscriptionScopeOf(preferred) === 'AGENCY' && rawPlanId) {
                            try {
                                const plan = await resolvePlanRecord(rawPlanId);
                                const agencyName = plan?.agencyName?.trim();
                                if (agencyName) planName = `${planName} · ${agencyName}`;
                            } catch {
                                /* sem o nome do orgao o rotulo fica so com o plano */
                            }
                            const outros = sorted.filter((sub) => sub !== preferred && isSubscriptionCurrent(sub) && subscriptionScopeOf(sub) === 'AGENCY').length;
                            if (outros > 0) planName = `${planName} +${outros}`;
                        }

                        return { supplierId, hasActiveSubscription: true, planName, validUntil } as const;
                    } catch (error) {
                        console.error('Failed to load subscription for supplier', { supplierId, error });
                        return { supplierId, hasActiveSubscription: false, planName: null, validUntil: null } as const;
                    }
                })
            );

            if (!mountedRef.current) {
                return;
            }

            const successful = results.filter((entry): entry is NonNullable<(typeof results)[number]> => Boolean(entry));
            if (!successful.length) {
                return;
            }

            setSubscriptionCache((previous) => {
                const next = { ...previous };
                let changed = false;

                for (const entry of successful) {
                    const payload = { hasActiveSubscription: entry.hasActiveSubscription, planName: entry.planName, validUntil: entry.validUntil };
                    const cached = next[entry.supplierId];
                    if (!cached || cached.hasActiveSubscription !== payload.hasActiveSubscription || cached.planName !== payload.planName || cached.validUntil !== payload.validUntil) {
                        next[entry.supplierId] = payload;
                        subscriptionCacheRef.current[entry.supplierId] = payload;
                        changed = true;
                    }
                }

                return changed ? next : previous;
            });
        },
        [resolvePlanRecord]
    );

    const applySelectedRepresentationId = useCallback(
        (updater: SelectedSetter, options?: { persist?: boolean; persistRemote?: boolean }) => {
            internalSetSelectedRepresentationId((current) => {
                const nextRaw = typeof updater === 'function' ? updater(current) : updater;
                let next = normalizeId(nextRaw);

                if (authReady && next === ADMIN_VIEW_CARD_ID && !canUseAdminView) {
                    next = CITIZEN_VIEW_CARD_ID;
                }

                if (current === next) {
                    return current;
                }
                if (options?.persist !== false) {
                    persistSelectedRepresentationId(next);
                }
                // Bug #92: persiste por CONTA no BE quando a troca foi acionada
                // pelo usuario (setSelectedRepresentationId publico). Hidratacao
                // interna do hydrateRepresentations passa persistRemote=false pra
                // nao gerar PATCH supérfluo.
                if (options?.persistRemote === true && authReady && !isGuest) {
                    persistSelectedRepresentationToAccount(next);
                }
                return next;
            });
        },
        [authReady, canUseAdminView, isGuest],
    );

    const hydrateRepresentations = useCallback(
        async (items: RepresentationSummary[], options?: SetRepresentationsOptions) => {
            const activeItems = items.filter(activeRepresentation);
            setRepresentationsState(activeItems);
            hasFetchedOnceRef.current = true;

            if (options?.forceEntityReload) {
                entityCacheRef.current = {};
                if (mountedRef.current) {
                    setEntityCache({});
                }
            }

            await loadRepresentationEntities(activeItems, { force: options?.forceEntityReload === true });
            await loadSupplierSubscriptions(activeItems);

            applySelectedRepresentationId(
                (current) => resolvePreferredRepresentationId(current, activeItems, authReady, canUseAdminView),
                { persist: true },
            );

            return activeItems;
        },
        [applySelectedRepresentationId, authReady, canUseAdminView, loadRepresentationEntities, loadSupplierSubscriptions],
    );

    const setRepresentations = useCallback(
        async (items: RepresentationSummary[], options?: SetRepresentationsOptions) => {
            return hydrateRepresentations(items, options);
        },
        [hydrateRepresentations],
    );

    const setSelectedRepresentationId = useCallback(
        (value: SelectedSetter) => {
            // persistRemote: true → grava no BE (PATCH /authentication/last-selected-representation).
            // So sai daqui, do setter publico — hidratacao interna nao precisa.
            applySelectedRepresentationId(value, { persist: true, persistRemote: true });
        },
        [applySelectedRepresentationId],
    );

    // Bug #92 (Flavio, 03/06/2026): apos login, se o usuario nao tem nada
    // selecionado no localStorage deste dispositivo, restaura o ultimo perfil
    // que ele usou pela CONTA (user.lastSelectedRepresentationId vindo do BE).
    //
    // Regras:
    //  - Guest nao tem conta -> nao aplica
    //  - LocalStorage tinha valor no mount -> escolha local prevalece, nao aplica
    //  - Ja aplicou uma vez -> nao re-aplica (idempotente)
    //  - user.lastSelectedRepresentationId null/vazio -> primeiro login, fica em Cidadao
    //  - Valor "revogado entre sessoes" (representacao deletada/desativada) ->
    //    hydrateRepresentations a seguir chama resolvePreferredRepresentationId
    //    que ja faz fallback pra citizen quando nao casa nos activeItems.
    useEffect(() => {
        if (!authReady || !user || isGuest) return;
        if (hydratedFromAccountRef.current) return;
        if (!localStorageWasEmptyAtMount.current) return;

        const fromAccount = normalizeId(user.lastSelectedRepresentationId as string | null | undefined);
        if (!fromAccount) return;

        hydratedFromAccountRef.current = true;
        // persist:true grava no localStorage (vira escolha local pro F5 seguinte).
        // persistRemote:false NAO chama o PATCH (valor ja vem do BE, evita echo).
        applySelectedRepresentationId(fromAccount, { persist: true, persistRemote: false });
    }, [authReady, user, isGuest, applySelectedRepresentationId]);

    const refreshRepresentations = useCallback(async (force = false) => {
        if (isGuest) {
            setRepresentationsState([]);
            hasFetchedOnceRef.current = true;
            setLoading(false);
            setError(null);
            applySelectedRepresentationId(CITIZEN_VIEW_CARD_ID, { persist: true });
            return [];
        }

        // O payload de login pode trazer userId, mas o portal consome o valor
        // normalizado em auth.user.id. Evita refresh prematuro antes da hidratação.
        if (!authReady || !user?.id) {
            setLoading(false);
            setError(null);
            return [];
        }

        setLoading(true);
        setError(null);
        try {
            const items = await userSharedService.listRepresentationsForPortal(undefined, { force, active: true, representativeUserId: user?.id });
            const activeItems = Array.isArray(items) ? items.filter(activeRepresentation) : [];
            return await hydrateRepresentations(activeItems, { forceEntityReload: force });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Não foi possível carregar as representações.';
            setError(message);
            return [];
        } finally {
            // Em erro nao marcar "ja buscou": isso impede novas tentativas e prende a home no vazio.
            setLoading(false);
        }
    }, [applySelectedRepresentationId, authReady, hydrateRepresentations, isGuest, user?.id]);

    // Detecta troca de usuário (logout/login) e limpa os caches/estados para evitar
    // contaminação de dados entre usuários diferentes.
    // Importante: em expiração de sessão seguida de relogin com o MESMO usuário,
    // não sobrescrever a visualização escolhida persistida no localStorage.
    useEffect(() => {
        const currentUserId = user?.id;
        const previousUserId = previousUserIdRef.current;

        // Primeira renderização: apenas registra o ID inicial, sem resetar
        if (previousUserId === undefined) {
            previousUserIdRef.current = currentUserId;
            return;
        }

        // Sem mudança de usuário: nada a fazer
        if (previousUserId === currentUserId) {
            return;
        }

        // Troca de usuário detectada — limpa caches de módulo e estado do provider
        previousUserIdRef.current = currentUserId;
        userSharedService.clearUserCaches();
        hasFetchedOnceRef.current = false;
        entityCacheRef.current = {};
        subscriptionCacheRef.current = {};
        setEntityCache({});
        setSubscriptionCache({});
        setRepresentationsState([]);

        // Ao deslogar (currentUserId null), preserva a selecao persistida.
        // Isso evita voltar sempre para cidadao quando a sessao expira.
        if (!currentUserId) {
            return;
        }

        // Em troca real de conta (usuario anterior e atual diferentes), reseta por
        // seguranca — mas SEM gravar 'citizen-view' no localStorage: limpa a chave
        // e rearma os refs pra conta nova hidratar do proprio
        // user.lastSelectedRepresentationId (Bug #92 follow-up, 10/06/2026).
        if (previousUserId && previousUserId !== currentUserId) {
            applySelectedRepresentationId(CITIZEN_VIEW_CARD_ID, { persist: false });
            persistSelectedRepresentationId(null);
            localStorageWasEmptyAtMount.current = true;
            hydratedFromAccountRef.current = false;
        }

        // Se o novo usuário já está logado, força recarregamento das representações
        if (!isGuest) {
            void refreshRepresentations(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGuest, user?.id]);

    useEffect(() => {
        if (!isGuest) {
            return;
        }

        userSharedService.clearUserCaches();
        hasFetchedOnceRef.current = true;
        entityCacheRef.current = {};
        subscriptionCacheRef.current = {};
        setEntityCache({});
        setSubscriptionCache({});
        setRepresentationsState([]);
        setLoading(false);
        setError(null);
        // Bug #92 follow-up (10/06/2026): ao virar guest (logout/sessao expirada)
        // NAO gravar 'citizen-view' no localStorage. Esse valor "fantasma" fazia o
        // storage parecer uma escolha local no proximo login, pulando a restauracao
        // por conta (user.lastSelectedRepresentationId) — o usuario caia sempre em
        // Cidadao. Limpa a chave e rearma os refs pra proxima sessao hidratar do BE.
        applySelectedRepresentationId(CITIZEN_VIEW_CARD_ID, { persist: false });
        persistSelectedRepresentationId(null);
        localStorageWasEmptyAtMount.current = true;
        hydratedFromAccountRef.current = false;
    }, [applySelectedRepresentationId, isGuest]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleStorage = (event: StorageEvent) => {
            if (event.storageArea !== window.localStorage || event.key !== SELECTED_STORAGE_KEY) return;
            applySelectedRepresentationId(() => normalizeId(event.newValue), { persist: false });
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [applySelectedRepresentationId]);

    // Assinatura contratada/ativada -> re-consulta o status do plano por fornecedor e atualiza o
    // cache, para o modal de selecao de visualizacao refletir o novo plano sem exigir F5/relogin.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleSubscriptionChange = () => {
            if (isGuest || !authReady || !user?.id) return;
            subscriptionCacheRef.current = {};
            void refreshRepresentations(true);
        };
        window.addEventListener(PORTAL_SUBSCRIPTION_CHANGE_EVENT, handleSubscriptionChange);
        return () => window.removeEventListener(PORTAL_SUBSCRIPTION_CHANGE_EVENT, handleSubscriptionChange);
    }, [authReady, isGuest, refreshRepresentations, user?.id]);

    useEffect(() => {
        if (isGuest || !authReady || !user?.id) {
            return;
        }

        const alreadyPopulated = representations.length > 0;
        if (!hasFetchedOnceRef.current && !loading && !alreadyPopulated) {
            void refreshRepresentations(false);
        }
    }, [authReady, isGuest, loading, refreshRepresentations, representations.length, user?.id]);

    const representationCards = useMemo<RepresentationSelectionCard[]>(() => {
        const citizenCard: RepresentationSelectionCard = {
            id: CITIZEN_VIEW_CARD_ID,
            name: 'Acesso como cidadão',
            document: null,
            secondaryNameLabel: null,
            secondaryNameValue: null,
            representationType: null,
            representationTypeLabel: 'Acesso como cidadão',
            entityType: null,
            entityId: null,
            entitySupplierId: null,
            hasActiveSubscription: false,
            subscriptionPlanName: null,
        };

        const adminCard: RepresentationSelectionCard = {
            id: ADMIN_VIEW_CARD_ID,
            name: 'Acesso como administrador',
            document: null,
            secondaryNameLabel: null,
            secondaryNameValue: null,
            representationType: null,
            representationTypeLabel: 'Acesso como administrador',
            entityType: null,
            entityId: null,
            entitySupplierId: null,
            hasActiveSubscription: false,
            subscriptionPlanName: null,
        };

        const mapped = representations.map((summary) => {
            const binding = resolveEntityBinding(summary);
            const cacheKey = binding ? `${binding.type}:${binding.id}` : null;
            const entityInfo = cacheKey ? entityCache[cacheKey] : undefined;
            const rawName = summary.label?.trim() ?? '';
            const document = entityInfo?.document ? DocumentNumberUtils.formatCnpj(entityInfo.document) : null;
            const typeLabel = summary.representationType ? (representationTypeLabels.get(summary.representationType) ?? summary.representationType) : null;
            // FIX9: em card de órgão público, mostra a FUNÇÃO do servidor
            // (papel) em vez do tipo jurídico da representação.
            const agencyRoleLabel = (entityInfo?.entityType ?? binding?.type) === 'public_agency'
                ? resolveAgencyRoleLabel(user?.roles as string[] | undefined)
                : null;
            const normalizedName = rawName ? (representationTypeLabels.get(rawName) ?? rawName) : '';
                const displayName = entityInfo?.name?.trim() || 'Nome não pode ser carregado';
            
            // Usa entitySupplierId para buscar no subscriptionCache (mesma chave usada ao popular o cache)
            const normalizedSupplierId = normalizeSupplierId(summary.entitySupplierId);
            const subscription = binding?.type === 'supplier' && normalizedSupplierId ? subscriptionCache[normalizedSupplierId] : undefined;
            const hasActiveSubscription = subscription?.hasActiveSubscription ?? false;
            const subscriptionPlanName = subscription?.planName ?? null;
            const subscriptionValidUntil = subscription?.validUntil ?? null;

            return {
                id: summary.id,
                name: displayName,
                document,
                secondaryNameLabel: entityInfo?.secondaryName?.label ?? null,
                secondaryNameValue: entityInfo?.secondaryName?.value ?? null,
                representationType: summary.representationType ?? null,
                representationTypeLabel: resolveCardRoleLabel(summary.representationType) ?? agencyRoleLabel ?? typeLabel,
                entityType: entityInfo?.entityType ?? binding?.type ?? null,
                entityId: binding?.id ?? null,
                entitySupplierId: summary.entitySupplierId ?? null,
                hasActiveSubscription,
                subscriptionPlanName,
                subscriptionValidUntil,
            } satisfies RepresentationSelectionCard;
        });

        return canUseAdminView ? [citizenCard, adminCard, ...mapped] : [citizenCard, ...mapped];
    }, [canUseAdminView, entityCache, representationTypeLabels, representations, resolveEntityBinding, subscriptionCache, user]);

    const requestRepresentationChange = useCallback(
        (nextId: string) => {
            const normalized = normalizeId(nextId);
            if (!normalized || normalized === selectedRepresentationId) return;

            // When running in the portal shell, dispatch a modal request event so the layout can show confirmation.
            const dispatched = (() => {
                if (typeof window === 'undefined') return false;
                try {
                    const event = new CustomEvent<string>(PORTAL_REPRESENTATION_CHANGE_EVENT, { detail: normalized });
                    window.dispatchEvent(event);
                    return true;
                } catch (error) {
                    console.warn('Failed to dispatch representation change event', error);
                    return false;
                }
            })();

            if (!dispatched) {
                setSelectedRepresentationId(normalized);
            }
        },
        [selectedRepresentationId, setSelectedRepresentationId],
    );

    useEffect(() => {
        if (authReady && !canUseAdminView && selectedRepresentationId === ADMIN_VIEW_CARD_ID) {
            applySelectedRepresentationId(CITIZEN_VIEW_CARD_ID, { persist: true });
        }
    }, [applySelectedRepresentationId, authReady, canUseAdminView, selectedRepresentationId]);

    const value = useMemo<PortalRepresentationSelectionContextValue>(
        () => ({
            representations,
            selectedRepresentationId,
            setRepresentations,
            setSelectedRepresentationId,
            requestRepresentationChange,
            refreshRepresentations,
            loading,
            error,
            representationCards,
        }),
        [error, loading, refreshRepresentations, representationCards, representations, requestRepresentationChange, selectedRepresentationId, setRepresentations, setSelectedRepresentationId],
    );

    return (
        <PortalRepresentationSelectionContext.Provider value={value}>
            {children}
        </PortalRepresentationSelectionContext.Provider>
    );
}

let warnedMissingProvider = false;

const fallbackSelectionContext: PortalRepresentationSelectionContextValue = {
    representations: [],
    selectedRepresentationId: null,
    setRepresentations: async () => [],
    setSelectedRepresentationId: () => {},
    requestRepresentationChange: () => {},
    refreshRepresentations: async () => [],
    loading: false,
    error: null,
    representationCards: [],
};

export function usePortalRepresentationSelection(): PortalRepresentationSelectionContextValue {
    const context = useContext(PortalRepresentationSelectionContext);
    if (!context) {
        if (!warnedMissingProvider) {
            // eslint-disable-next-line no-console
            console.warn('usePortalRepresentationSelection used without RepresentationSelectionProvider. Using fallback context.');
            warnedMissingProvider = true;
        }
        return fallbackSelectionContext;
    }
    return context;
}
